import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Formik, Form, Field, useFormikContext } from 'formik';
import mixpanel from 'mixpanel-browser';

// UTILS
import { intercom } from 'utils';

// HOOKS
import { useAppDispatch } from 'hooks/store';

// CONSTANTS
import { FLASH_MESSAGE_TYPES } from 'constants/common';
import { ANALYTICS_EVENTS } from 'constants/mixpanel.constants';

// ASSETS
import PaymentIcon from 'assets/PaymentIcon';
import LevelBelowIcon from 'assets/LevelBelowIcon';

// COMPONENTS
import FlashMessage from 'components/FlashMessage';
import { SaveButton } from 'components/Buttons';
import { ToggleInput } from 'components/FormField';

// SERVICES
import { selectFleetSettings, selectLoading } from 'services/FleetSettings/selectors';
import { getFleetSettings, updateSettings } from 'services/FleetSettings/thunks';
import { FleetSettings } from 'services/resources';

// STYLES
import { Description } from './styles';
import { DepenentInputContainer, IconContainer, InputContainer } from '../../FleetSettings.styles';
import { Section, SectionHeader, SectionContent, SectionHeaderTitle, SectionHeaderActions } from 'styles/cards';
import colors from 'styles/colors';
import { selectSelectedHierarchyEntity } from 'services/Hierarchy/selectors';

const useHybridPaymentEntry = () => {
  const { values, setFieldValue } = useFormikContext<FleetSettings>();

  useEffect(() => {
    if (!values.stripeManagedCreditcards && values.hybridPaymentEntry) {
      setFieldValue('hybridPaymentEntry', false);
    }
  }, [values.stripeManagedCreditcards, values.hybridPaymentEntry, setFieldValue]);

  return [];
};

const disableSave = <T extends Pick<FleetSettings, 'stripeManagedCreditcards' | 'hybridPaymentEntry'>>(
  initialValues: T,
  values: T
) => {
  return (
    initialValues.stripeManagedCreditcards === values.stripeManagedCreditcards &&
    initialValues.hybridPaymentEntry === values.hybridPaymentEntry
  );
};

const PaymentForm = ({ isSubmitting, values, initialValues }) => {
  const intl = useIntl();
  const isLoading = useSelector(selectLoading());
  useHybridPaymentEntry();

  return (
    <Form>
      <SectionHeader>
        <SectionHeaderTitle>
          <PaymentIcon color={colors.fleetBlue} />
          <FormattedMessage id="views.FleetSettings.PaymentDetails.title" />
        </SectionHeaderTitle>
        <SectionHeaderActions>
          <SaveButton loading={isLoading || isSubmitting} disabled={disableSave(initialValues, values)} type="submit" />
        </SectionHeaderActions>
      </SectionHeader>
      <SectionContent>
        <Description>
          <FormattedMessage id="views.FleetSettings.PaymentDetails.description" />
        </Description>

        <InputContainer>
          <Field
            as={ToggleInput}
            type="checkbox"
            name="stripeManagedCreditcards"
            inlineLabel={intl.formatMessage({
              id: 'views.FleetSettings.PaymentDetails.enableStripeManagedCC',
            })}
            style={{ marginLeft: '10px' }}
          />
        </InputContainer>

        {values.stripeManagedCreditcards && (
          <DepenentInputContainer>
            <IconContainer>
              <LevelBelowIcon />
            </IconContainer>
            <Field
              as={ToggleInput}
              type="checkbox"
              name="hybridPaymentEntry"
              inlineLabel={intl.formatMessage({
                id: 'views.FleetSettings.PaymentDetails.enableHybridPaymentEntry',
              })}
              style={{ marginLeft: '10px' }}
            />
          </DepenentInputContainer>
        )}
      </SectionContent>
    </Form>
  );
};

const FleetSettingsPaymentDetailsView = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const fleetSettings = useSelector(selectFleetSettings());

  const selectedHierarchyEntity = useSelector(selectSelectedHierarchyEntity());
  useEffect(() => {
    dispatch(getFleetSettings());
  }, [dispatch, selectedHierarchyEntity]);

  const [flashMessageVisible, setFlashMessageVisible] = useState(false);
  const [flashMessageType, setFlashMessageType] = useState(null);
  const [flashMessage, setFlashMessage] = useState(null);

  const handleOpenFlashMessage = (text, type) => {
    setFlashMessageVisible(true);
    setFlashMessageType(type);
    setFlashMessage(text);
  };

  const handleCloseFlashMessage = () => {
    setFlashMessageVisible(false);
    setFlashMessageType(null);
    setFlashMessage(null);
  };

  const handleSave = (values) => {
    return dispatch(
      updateSettings(fleetSettings.get('id'), {
        stripeManagedCreditcards: values.stripeManagedCreditcards,
        hybridPaymentEntry: values.hybridPaymentEntry,
      })
    )
      .then(() => {
        intercom.trackEvent('Updated Payment Details', {});
        if (fleetSettings.get('stripeManagedCreditcards') !== values.stripeManagedCreditcards) {
          if (values.stripeManagedCreditcards) {
            mixpanel.track(ANALYTICS_EVENTS.CONTACTLESS_PAYMENT_ENABLED);
          } else {
            mixpanel.track(ANALYTICS_EVENTS.CONTACTLESS_PAYMENT_DISABLED);
          }
        }
        if (fleetSettings.get('hybridPaymentEntry') !== values.hybridPaymentEntry) {
          if (values.hybridPaymentEntry) {
            mixpanel.track(ANALYTICS_EVENTS.ALLOW_CC_MANUAL_ENTRY_ENABLED);
          } else {
            mixpanel.track(ANALYTICS_EVENTS.ALLOW_CC_MANUAL_ENTRY_DISABLED);
          }
        }
        handleOpenFlashMessage(
          intl.formatMessage({ id: 'views.FleetSettings.PaymentDetails.update.success' }),
          FLASH_MESSAGE_TYPES.success
        );
      })
      .catch(() => {
        handleOpenFlashMessage(
          intl.formatMessage({ id: 'views.FleetSettings.PaymentDetails.update.error' }),
          FLASH_MESSAGE_TYPES.error
        );
      });
  };

  const initialValues = {
    stripeManagedCreditcards: fleetSettings.get('stripeManagedCreditcards'),
    hybridPaymentEntry: fleetSettings.get('hybridPaymentEntry'),
  };

  return (
    <Section>
      <FlashMessage
        visible={flashMessageVisible}
        type={flashMessageType}
        text={flashMessage}
        onClose={handleCloseFlashMessage}
      />
      <Formik initialValues={initialValues} onSubmit={handleSave} enableReinitialize>
        {(props) => <PaymentForm {...props} />}
      </Formik>
    </Section>
  );
};

export default FleetSettingsPaymentDetailsView;
